/** Constants for camera capture quality */
export const GALLERY_MAX_SIZE = 1048576; // 1MB
export const CAMERA_MAX_SIZE = 5242880; // 5MB
/**
 * Blur detection threshold. Laplacian variance below this indicates blur.
 * Range: 30–150 depending on device camera, lighting, and compression.
 * Typical values:
 *   - Severely blurry (out of focus): 5–30
 *   - Slightly blurry: 30–50
 *   - Acceptable focus (most devices): 50–100
 *   - Very sharp focus: 100+
 * Device cameras often score 50-80 naturally. Set to 70 to accept good photos.
 */
export const BLUR_THRESHOLD = 50;

/**
 * Enable on-screen sharpness score display for debugging.
 * Set to true to see actual sharpness values in a debug badge.
 */
export const DEBUG_BLUR_DETECTION = false;
export const COUNTDOWN_SECONDS = 2;

// Prefer higher camera sensor resolution while still allowing browser fallback.
export const WEBCAM_CAPTURE_SIZE = {
  width: 1920,
  height: 1080,
};

/**
 * Camera constraints optimized for document (cheque) capture.
 * - No aspectRatio constraint — lets the camera use its native 4:3 sensor
 *   for the widest possible field of view (matching the native camera app).
 * - The display uses object-fit: cover for framing, and capture crops to
 *   the visible area automatically.
 * - Uses continuous autofocus so the camera adapts to document distance.
 * - Explicit zoom: 1 to prevent any digital zoom.
 * - No torch to avoid glare on glossy paper.
 */
export const WEBCAM_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: "environment",
  width: { ideal: 1920, min: 1280 },
  height: { ideal: 1080, min: 720 },
  advanced: [
    { focusMode: "continuous" } as any,
    { exposureMode: "continuous" } as any,
    { whiteBalanceMode: "continuous" } as any,
    { zoom: 1 } as any,
  ],
};

/**
 * Computes the Laplacian variance of a canvas image to detect blur.
 * A higher value indicates a sharper image; lower values indicate blur.
 *
 * Uses a 3×3 Laplacian kernel:
 *  [ 0,  1,  0]
 *  [ 1, -4,  1]
 *  [ 0,  1,  0]
 *
 * @returns Variance of the Laplacian — values below BLUR_THRESHOLD indicate a blurry image.
 */
export function computeLaplacianVariance(canvas: HTMLCanvasElement): number {
  const ctx = canvas.getContext("2d");
  if (!ctx) return 0;

  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  // Convert to grayscale array
  const gray = new Float32Array(width * height);
  for (let i = 0; i < gray.length; i++) {
    const offset = i * 4;
    gray[i] = 0.299 * data[offset] + 0.587 * data[offset + 1] + 0.114 * data[offset + 2];
  }

  // Apply 3×3 Laplacian kernel (skip border pixels)
  let sum = 0;
  let sumSq = 0;
  let count = 0;

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      const laplacian =
        gray[idx - width] +       // top
        gray[idx - 1] +           // left
        -4 * gray[idx] +          // center
        gray[idx + 1] +           // right
        gray[idx + width];        // bottom

      sum += laplacian;
      sumSq += laplacian * laplacian;
      count++;
    }
  }

  if (count === 0) return 0;

  const mean = sum / count;
  return sumSq / count - mean * mean;
}
